import LoggingBase from '../base/loggingbase';
import Validator from './Validator';

/**
 * class ExternalEventsTransferManager - decorates a widget's parent DOM element with a
 * `_transferDomEvent` function so that DOM events captured elsewhere (e.g. by the global
 * key handler) can be transferred back into the widget. It is a plain delegate: the host
 * widget is passed in and accessed via {@link #host}; the transferred event is handed to
 * the host's `handleTransferredEvent` method (if it has one).
 */
export default class ExternalEventsTransferManager extends LoggingBase {

	/**
	 * constructs a new instance
	 * @param {*} host the host widget
	 * @param {HTMLElement} parentElement the widget's parent DOM element to decorate
	 */
	constructor( host, parentElement ) {
		super( 'utils.ExternalEventsTransferManager' );
		this._host = host;
		this.decorateParentElement( parentElement );
	}

	/**
	 * @returns {*} the host widget
	 */
	get host() {
		return this._host;
	}

	/**
	 * @override
	 */
	doDestroy() {
		this._host = void 0;
		super.doDestroy();
	}

	decorateParentElement( parentElement ) {
		if ( !( parentElement instanceof HTMLElement ) ) {
			this.trace( `Could not add a method to transfer DOM` +
				` events to the parent element of the widget, because the parent` +
				` element does not exist.` );
			return false;
		}
		const self = this;
		if ( Validator.isFunction( parentElement._transferDomEvent ) ) {
			this.trace( `The DOM parent element of the widget already` +
				` has a method that transfers DOM events. The function will be` +
				` overriden with a new one that accesses the current widget.` );
		}
		parentElement._transferDomEvent = ( evt ) => {
			if ( !( evt instanceof Event ) ) {
				self.trace( `Could not transfer an invalid DOM event to` +
					` the widget.` );
				return false;
			}
			const host = self.host;
			if ( !Validator.isObject( host ) ) {
				self.trace( `There is no widget to transfer the DOM event to.` );
				return false;
			}
			if ( !Validator.isFunction( host.handleTransferredEvent ) ) {
				self.trace( `The DOM event could not be transferred to` +
					` the corresponding widget, because the widget does not have a` +
					` method to handle transferred DOM events.` );
				return false;
			}
			return host.handleTransferredEvent( evt );
		}
		return true;
	}
}
